// Modules
import { Link } from 'react-router';
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import moment from 'moment';

import { SHORT_HUMAN_DATE_TIME_FORMAT } from 'constants';
import { ShortDate } from 'components/widgets/dates';
import { alerts } from 'modules/alerts/actions';
import Call from 'services/data/call';
import CompanyNickname from 'components/models/company/company-nickname';
import Email from 'services/data/email';
import EmailMeta from 'components/models/email/emailMeta';
import MonitoringAlert from 'services/data/monitoring-alert';
import Person from 'services/data/person';
import PhoneNumber from 'services/data/phone-number';
import SVG from 'components/widgets/icons/svg';
import TicketLink from 'components/models/ticket/ticket-link';
import Tooltip from 'components/widgets/overlays/tooltip';
import Truncate from 'components/widgets/text/truncate';
import UserInitials from 'components/models/user/user-initials';

import RecordingLink from './recording-link';
import SendToInbox from './send-to-inbox';


export default class TriageRow extends React.Component {
  static propTypes = {
    item: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired
  };

  constructor() {
    super();

    this.state = { claimedBy: null, archived: null };
    this.claim = this.claim.bind(this);
    this.archive = this.archive.bind(this);
    this.restore = this.restore.bind(this);
  }

  getItemOrigin(item) {
    switch (item.type) {
      case 'call':
        return PhoneNumber.format(item.from);
      case 'email':
        return item.parsedFrom.name || item.parsedFrom.address;
      case 'monitoringalert':
        return item.hostname;
      default:
        break;
    }
  }

  getPayload(item) {
    switch (item.type) {
      case 'call':
        return item.callStatus;
      case 'email':
        return item.subject;
      case 'monitoringalert':
        return item.trigger;
      default:
        break;
    }
  }

  getTypeIcon(item) {
    switch (item.type) {
      case 'call':
        return 'svg/phone.svg';
      case 'email':
        return 'svg/email.svg';
      case 'monitoringalert':
        return 'svg/monitoring-alert.svg';
      default:
        break;
    }
  }

  getStatusTooltip(item) {
    const claimed = !_.isEmpty(item.claimedBy);
    const status = (item.status === 'Active' && claimed) ? 'Claimed' : item.status;

    if (item.type === 'monitoringalert' && item.autoResolved) return `${status} - Auto Resolved`;
    return status;
  }

  getMonitoringAlertFullPayload(item) {
    const isAutoSrvcs = _.startsWith(item.trigger, 'Auto svcs stopped');
    const isDiskSpace = _.startsWith(item.trigger, 'Free space');
    
    if (isAutoSrvcs || isDiskSpace) return `${item.trigger}: ${item.value.replace(/ /g, ', ')}`;
    return item.trigger;
  }

  claim(item) {
    const user = this.props.currentUser;
    const typeLabels = { call: 'Call', email: 'Email', monitoringalert: 'Monitoring alert' };

    this.setState({ claimedBy: user });

    const claimService = { call: Call, email: Email, monitoringalert: MonitoringAlert };
    const service = claimService[item.type];

    if (!service) return;

    return service.claim(item.id)
      .catch(error => {
        if (!error.response || error.response.status !== 404) return;

        this.setState({ claimedBy: null });
        alerts.warning({ title: `${typeLabels[item.type]} has already been claimed` });
      });
  }

  archive(item) {
    this.setState({ archived: true });
    this.update(item, { status: 'Archived' });
  }

  restore(item) {
    this.setState({ archived: false });
    this.update(item, { status: 'Active' });
  }

  update(item, updates) {
    switch (item.type) {
      case 'call':
        return Call.update({ id: item.id, ...updates });
      case 'email':
        return Email.update({ id: item.id, ...updates });
      case 'monitoringalert':
        return MonitoringAlert.update({ id: item.id, ...updates });
      default:
        break;
    }
  }

  render() {
    const { item, currentUser } = this.props;
    const isEmail = item.type === 'email';
    const isAlert = item.type === 'monitoringalert';
    const isCall = item.type === 'call';
    const isArchived = this.state.archived !== null ? this.state.archived : item.status === 'Archived';
    const hasTicket = item.ticket && item.ticket.id;
    const payload = this.getPayload(item);
    const fullPayload = isAlert ? this.getMonitoringAlertFullPayload(item) : payload;
    const hasRecording = isCall && item.callStatus === 'Recorded Message';
    const claimedBy = this.state.claimedBy || item.claimedBy;
    const claimed = !_.isEmpty(claimedBy);

    const origin = _.isEmpty(item.fromPerson) ? this.getItemOrigin(item) : Person.fullName(item.fromPerson);
    
    return (
      <tr className={classNames({ 'disabled-row': isArchived })}>
        {/* Type/Status */}
        <td className={classNames('type-status-container', _.kebabCase(item.status), { claimed: !_.isEmpty(item.claimedBy) })}>
          <Tooltip className="triage-type-status" title={this.getStatusTooltip(item)}>
            <SVG src={this.getTypeIcon(item)} />
          </Tooltip>
        </td>
        {/* Date */}
        <td>
          <ShortDate title={moment(item.createdAt).format(SHORT_HUMAN_DATE_TIME_FORMAT)} date={item.createdAt} />
        </td>
        {/* Action icons */}
        <td className="icon-cell">
          <div className="flex">
            { isArchived &&
              <Tooltip title="Restore">
                <SVG className="pointer" src="svg/refresh.svg" onClick={() => this.restore(item)} />
              </Tooltip>
            }
            { !isArchived &&
              <Tooltip title="Archive">
                <SVG className="pointer" src="svg/delete.svg" onClick={() => this.archive(item)} />
              </Tooltip>
            }
            { isEmail && <SendToInbox emailId={item.id} currentUser={currentUser} /> }
            { !claimed &&
              <Tooltip title="Claim">
                <SVG className="pointer claim" src="svg/claim.svg" onClick={() => this.claim(item)} />
              </Tooltip>
            }
            { hasRecording && <RecordingLink item={item} /> }
          </div>
        </td>
        <td>
          { claimed && <UserInitials user={claimedBy} /> }
          
        </td>
        <td className="expand-cell"><Truncate tooltip={fullPayload}>{payload}</Truncate></td>
        <td className="expand-cell">
          { isEmail &&
            <Truncate tooltip={<EmailMeta email={item} />} textContent={origin}>
              <Link to={`/people/${item.fromPerson.id}`}>{origin}</Link>
            </Truncate>
          }
          { !isEmail && <Truncate>{origin}</Truncate>}
        </td>
        <td><CompanyNickname company={item.company} /></td>
        { hasTicket &&
          <td className="expand-cell">
            <TicketLink ticket={item.ticket}>
              <Truncate>{item.ticket.description}</Truncate>
            </TicketLink>
          </td>
        }
        { !hasTicket && <td></td> }
        
      </tr>
    );
  }
}
